import React, { useEffect, useRef } from 'react';
import { isMultiple } from '../../components/utils';
import { Icon, minus_sign, plus_sign } from '../Icon/Icon';
import { Button } from './Button';
import './QuantityInput.css';

type QuantityInputProps = {
	onBlur: (defaultValue: number) => void;
	defaultValue: number;
	step?: number;
	iconSize?: number;
	allowZero?: boolean;
	disabled?: boolean;
	maxValue?: number;
	minValue?: number;
};

export const QuantityInput = ({
	defaultValue,
	onBlur,
	step = 1,
	iconSize,
	allowZero = false,
	disabled,
	maxValue,
	minValue,
}: QuantityInputProps): JSX.Element => {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (inputRef.current) inputRef.current.value = defaultValue + '';
	}, [defaultValue]);

	function modify(delta: number) {
		if (inputRef.current) {
			const value = +inputRef.current.value + delta;
			inputRef.current.value = value.toString();
			onBlur(value);
		}
	}

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>, inputRef: React.RefObject<HTMLInputElement>): void => {
		const quantity = +e.target.value;

		if (quantity < 0) {
			if (inputRef.current) inputRef.current.value = defaultValue + '';
			return;
		}

		if (maxValue !== undefined && quantity >= maxValue) {
			if (inputRef.current) inputRef.current.value = maxValue + '';
			return onBlur(maxValue);
		}

		if (quantity === 0 && !allowZero) {
			return onBlur(quantity);
		}

		if (step > 1) {
			if (isMultiple(step, quantity)) {
				return onBlur(quantity);
			} else {
				if (inputRef.current) {
					const closestOptimisticallyRoundedMultiple = Math.ceil(quantity / step) * step;
					inputRef.current.value = closestOptimisticallyRoundedMultiple + '';

					return onBlur(closestOptimisticallyRoundedMultiple);
				}
			}
		} else {
			return onBlur(quantity);
		}
	};

	const decreaseDisabled = (): boolean => {
		if (disabled) return true;

		if (minValue === undefined) {
			if (allowZero) {
				return defaultValue <= 0;
			} else {
				return defaultValue <= step;
			}
		}

		if (inputRef.current) {
			const value = +inputRef.current?.value;
			if (minValue === value) {
				return true;
			}
		}

		return false;
	};

	const increaseDisabled = (): boolean => {
		if (disabled) return true;
		if (maxValue === undefined || !inputRef.current) {
			return false;
		}

		const value = +inputRef.current?.value;

		if (maxValue <= value) {
			return true;
		}

		return false;
	};

	return (
		<quantity-input className={disabled ? 'disabled' : ''}>
			<Button onClick={() => modify(-step)} disabled={decreaseDisabled()} className="decrease-quantity">
				<Icon shape={minus_sign} size={iconSize ?? 22} />
			</Button>
			<input
				ref={inputRef}
				type="number"
				min={allowZero ? 0 : step}
				max={maxValue}
				step={step}
				disabled={disabled}
				onBlur={(e) => handleBlur(e, inputRef)}
				defaultValue={defaultValue}
			/>
			<Button onClick={() => modify(step)} disabled={increaseDisabled()} className="increase-quantity">
				<Icon shape={plus_sign} size={iconSize ?? 22} />
			</Button>
		</quantity-input>
	);
};
